import * as React from 'react';

import { translate } from '~l10n/client';

import { toast } from '~shared/components/Toast';
import { useSocrativeRouter } from '~teacher/context/RouterContext';
import { useWorkspacePermissionsV2 } from '~teacher/hook/workspaces';
import { useChangeWorkspaceRole } from '~teacher/hook/workspaces/useChangeWorkspaceRole';
import { useGetWorkspaceRole } from '~teacher/hook/workspaces/useGetWorkspaceRole';
import { useRemoveWorkspaceMember } from '~teacher/hook/workspaces/useRemoveWorkspaceMember';
import { useTransferWorkspaceOwnership } from '~teacher/hook/workspaces/useTransferWorkspaceOwnership';
import { useWorkspaceMembers } from '~teacher/hook/workspaces/useWorkspaceMembers';

import { isAxiosError } from '../../../../../api/isAxiosError';
import { WorkspaceRole } from '../../../../../api/workspaces/getWorkspaceMembers';

/**
 * unified hook for handling workspace members actions
 * - independent action for removing a workspace member
 * - transfer ownership of workspace, trigger modal
 */
export const useWorkspaceActions = (workspaceId: string) => {
  const socrativeRouter = useSocrativeRouter();
  const {
    permissions: {
      editMemberRoles: canEditMemberRoles,
      transferWorkspaceOwnership: canTransferWorkspaceOwnership,
      removeMemberFromWorkspace: canRemoveMemberFromWorkspace,
    },
  } = useWorkspacePermissionsV2({ workspaceId });

  const workspaceMembers = useWorkspaceMembers({
    workspaceId,
  });

  const requestUser = useGetWorkspaceRole({ workspaceId });
  const removeMemberMutation = useRemoveWorkspaceMember(workspaceId);
  const changeWorkspaceRoleMutation = useChangeWorkspaceRole(workspaceId);
  const transferMemberMutation = useTransferWorkspaceOwnership();

  // initial states
  const [isRemoveOpen, setIsRemoveOpen] = React.useState(false);
  const [isTransferOpen, setIsTransferOpen] = React.useState(false);
  const [isDisabled, setIsDisabled] = React.useState(false);

  // using refs to prevent these from changing when the op is not completed
  const userId = React.useRef<null | string>(null);
  const username = React.useRef<null | string>(null);

  // on close
  const onTransferClose = React.useCallback(() => {
    setIsTransferOpen(false);
  }, []);
  const onRemoveClose = React.useCallback(() => {
    setIsRemoveOpen(false);
  }, []);

  // actions
  const onTransfer = React.useCallback(() => {
    setIsDisabled(true);
    transferMemberMutation.mutate(
      {
        workspaceId,
        userId: userId.current,
      },
      {
        onSettled: () => {
          setIsDisabled(false);
          onTransferClose();
        },
        onError: () => toast.critical(translate('An error has occurred')),
      }
    );
  }, [onTransferClose, transferMemberMutation, workspaceId]);

  const onChangeRole = React.useCallback(
    ({ userId, role }: { userId: string; role: WorkspaceRole }) => {
      changeWorkspaceRoleMutation.mutate(
        {
          workspaceId,
          userId,
          role,
        },
        {
          onError: (err) => {
            if (isAxiosError(err)) {
              switch (err.response.status) {
                case 403: {
                  toast.critical(translate('An error has occurred'));
                  break;
                }
                default:
                  toast.critical(
                    'An error occurred while trying to change access level'
                  );
              }
            } else {
              toast.critical(
                'An error occurred while trying to change access level'
              );
            }
          },
        }
      );
    },
    [changeWorkspaceRoleMutation, workspaceId]
  );

  const onRemove = React.useCallback(() => {
    setIsDisabled(true);
    removeMemberMutation.mutate(
      {
        workspaceId,
        userId: userId.current,
      },
      {
        onSettled: () => {
          setIsDisabled(false);
        },
        onSuccess: (_data, { userId }) => {
          if (userId === requestUser.data?.id) {
            socrativeRouter.navigate('launch', {
              trigger: true,
              replace: true,
            });
          } else {
            onRemoveClose();
          }
        },
        onError: () => toast.critical(translate('An error has occurred')),
      }
    );
  }, [
    onRemoveClose,
    removeMemberMutation,
    requestUser.data?.id,
    socrativeRouter,
    workspaceId,
  ]);

  // action options
  const tableActions = React.useMemo(
    () => [
      {
        title: translate('Owner'),
        description: translate(
          'Can edit content, members, settings, and delete library.'
        ),
        searchKey: 'owner',
        key: 'owner',
        action: ({ close, data }) => {
          userId.current = data.id;
          username.current = workspaceMembers.data.find(
            (item) => item.id === data.id
          ).fullName;
          setIsTransferOpen(true);
          close();
        },
        constraint: () => canTransferWorkspaceOwnership,
      },
      {
        title: translate('Editor'),
        description: translate(
          'Can edit library content and invite other Editors and Viewers.'
        ),
        searchKey: 'editor',
        key: 'editor',
        action: ({ close, data }) => {
          onChangeRole({ userId: data.id, role: WorkspaceRole.Editor });
          close();
        },
        constraint: () => canEditMemberRoles,
      },
      {
        title: translate('Viewer'),
        description: translate(
          'Can only view and export library content. Can view members and invite other Viewers.'
        ),
        key: 'viewer',
        action: ({ close, data }) => {
          onChangeRole({ userId: data.id, role: WorkspaceRole.Viewer });
          close();
        },
        constraint: () => canEditMemberRoles,
      },
      {
        title: translate('Remove'),
        disabled: ({ data }) => {
          // Do not disable for the current Teacher
          if (data.id === requestUser.data?.id) {
            return false;
          }
          return !canRemoveMemberFromWorkspace;
        },
        description: translate('This member will lose access to this library.'),
        color: 'text-red-800',
        searchKey: 'remove',
        key: 'remove',
        action: ({ close, data }) => {
          userId.current = data.id;
          username.current = workspaceMembers.data.find(
            (item) => item.id === data.id
          ).fullName;
          setIsRemoveOpen(true);
          close();
        },
      },
    ],
    [
      workspaceMembers.data,
      canTransferWorkspaceOwnership,
      canEditMemberRoles,
      onChangeRole,
      requestUser.data?.id,
      canRemoveMemberFromWorkspace,
    ]
  );
  // exports
  return React.useMemo(
    () => ({
      isDisabled,
      onTransfer,
      onRemove,
      username: username.current,
      tableActions,
      isRemoveOpen,
      isTransferOpen,
      onTransferClose,
      onRemoveClose,
    }),
    [
      isDisabled,
      onTransfer,
      onRemove,
      username,
      tableActions,
      isRemoveOpen,
      isTransferOpen,
      onTransferClose,
      onRemoveClose,
    ]
  );
};
